import { CurrencyAmount, Token } from '@kodiak-finance/sdk-core'
import { ButtonConfirmed } from 'components/Button'
import CurrencyLogo from 'components/CurrencyLogo'
import { ResponsiveButtonPrimary } from 'components/PositionList/styled'
import { Dots } from 'components/swap/styleds'
import { MouseoverTooltip } from 'components/Tooltip'
import { useCollectFees } from 'hooks/useCollectFees'
import { useEffect, useRef } from 'react'
import styled, { useTheme } from 'styled-components'
import { ThemedText } from 'theme'
import { formatCurrencyAmount } from 'utils/formatCurrencyAmount'

const CollectButton = styled(ButtonConfirmed)`
  font-size: 12px;
  width: 80px;
  padding: 4px 8px;
`

const CollectButtonPortfolio = styled(ResponsiveButtonPrimary)`
  width: 160px;
  background-color: var(--color-kodiak-button-primary);
  border: 1px solid var(--color-kodiak-button-primary);
  color: white;

  &:hover {
    background-color: var(--color-kodiak-brand-secondary);
    border-color: var(--color-kodiak-brand-secondary);
  }

  ${({ theme }) => theme.mediaWidth.upToSmall`
    width: 100%;
  `}

  ${({ theme }) => theme.mediaWidth.upToSmall`
    font-size: 12px;
  `}
`

const FeeTooltipContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const FeeRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  min-width: 120px;
`

interface CollectFeesButtonProps {
  tokenId?: string
  token0?: Token
  token1?: Token
  feeValue0?: CurrencyAmount<Token>
  feeValue1?: CurrencyAmount<Token>
  fiatValueOfFees?: number | null
  owner?: string
  currentAccount?: string
  showTooltip?: boolean
  onCollectSuccess?: () => void
  variant?: 'compact' | 'portfolio'
}

export function CollectFeesButton({
  tokenId,
  token0,
  token1,
  feeValue0,
  feeValue1,
  owner,
  currentAccount,
  showTooltip = true,
  onCollectSuccess,
  variant = 'compact',
}: CollectFeesButtonProps) {
  const theme = useTheme()
  const notifiedCollectHashRef = useRef<string | null>(null)

  const { collect, collecting, collectMigrationHash, isCollectPending, canCollect } = useCollectFees({
    tokenId,
    currency0: token0,
    currency1: token1,
    feeValue0,
    feeValue1,
  })

  const ownsNFT = Boolean(currentAccount && owner === currentAccount.toLowerCase())

  const handleCollect = async (event: React.MouseEvent) => {
    event.stopPropagation()
    event.preventDefault()
    await collect()
  }

  useEffect(() => {
    if (!onCollectSuccess || !collectMigrationHash || isCollectPending) return
    if (notifiedCollectHashRef.current === collectMigrationHash) return

    notifiedCollectHashRef.current = collectMigrationHash
    onCollectSuccess()
  }, [collectMigrationHash, isCollectPending, onCollectSuccess])

  const buttonContent = () => {
    if (!!collectMigrationHash && !isCollectPending) {
      return variant === 'portfolio' ? (
        'Collected'
      ) : (
        <ThemedText.DeprecatedMain color={theme.textPrimary}>Collected</ThemedText.DeprecatedMain>
      )
    }

    if (isCollectPending || collecting) {
      return variant === 'portfolio' ? (
        <Dots>Collecting</Dots>
      ) : (
        <ThemedText.DeprecatedMain color={theme.textPrimary}>
          <Dots>Collecting</Dots>
        </ThemedText.DeprecatedMain>
      )
    }

    return variant === 'portfolio' ? (
      'Collect Fees'
    ) : (
      <ThemedText.DeprecatedMain color={theme.textPrimary}>Collect</ThemedText.DeprecatedMain>
    )
  }

  const tooltipContent =
    showTooltip && (feeValue0?.greaterThan(0) || feeValue1?.greaterThan(0)) ? (
      <FeeTooltipContent>
        <FeeRow>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CurrencyLogo currency={feeValue0?.currency} size="16px" className="mr-1.5" />
            <ThemedText.DeprecatedMain fontSize="12px">{feeValue0?.currency?.symbol}</ThemedText.DeprecatedMain>
          </div>
          <ThemedText.DeprecatedMain fontSize="12px">
            {feeValue0 ? formatCurrencyAmount(feeValue0, 4) : '0'}
          </ThemedText.DeprecatedMain>
        </FeeRow>
        <FeeRow>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <CurrencyLogo currency={feeValue1?.currency} size="16px" className="mr-1.5" />
            <ThemedText.DeprecatedMain fontSize="12px">{feeValue1?.currency?.symbol}</ThemedText.DeprecatedMain>
          </div>
          <ThemedText.DeprecatedMain fontSize="12px">
            {feeValue1 ? formatCurrencyAmount(feeValue1, 4) : '0'}
          </ThemedText.DeprecatedMain>
        </FeeRow>
      </FeeTooltipContent>
    ) : null

  const hasFees = feeValue0?.greaterThan(0) || feeValue1?.greaterThan(0)

  if (variant === 'portfolio' && (!ownsNFT || !hasFees)) {
    return null
  }

  const ButtonComponent = variant === 'portfolio' ? CollectButtonPortfolio : CollectButton
  const button = (
    <ButtonComponent
      disabled={collecting || !!collectMigrationHash || !ownsNFT || !canCollect}
      confirmed={!!collectMigrationHash && !isCollectPending}
      onClick={handleCollect}
    >
      {buttonContent()}
    </ButtonComponent>
  )

  if (!showTooltip || !tooltipContent) {
    return button
  }

  return <MouseoverTooltip text={tooltipContent}>{button}</MouseoverTooltip>
}
